import { getSessionData } from '@canvas/frame-api/get-session-data';

import { type RelaySupplierResult } from '../network/socket';
import {
  type Transport,
  type TransportCallbacks,
  type TransportContext,
  createCollabConnectionTransport,
} from '../transport';

import { type CollabStrategy } from './collab-strategy';

export class NcsCollabStrategy implements CollabStrategy {
  readonly type = 'ncs' as const;

  createTransport(ctx: TransportContext, callbacks: TransportCallbacks): Transport {
    return createCollabConnectionTransport(ctx, callbacks);
  }

  sendAwareness(
    message: Uint8Array<ArrayBuffer>,
    transport: Transport | undefined,
    fallbackSend: (msg: Uint8Array<ArrayBuffer>) => void
  ): void {
    if (transport?.sendAwareness) {
      transport.sendAwareness(message);
    } else {
      fallbackSend(message);
    }
  }

  getStepsUrl(params: {
    documentId: string;
    headStep: string;
    serverUrl?: string;
    attempt: number;
  }): string {
    const { documentId, headStep, serverUrl } = params;
    if (!serverUrl) {
      throw new Error('serverUrl is required for NCS steps URL');
    }
    return `${serverUrl}/api/_internal/document/${encodeURIComponent(
      documentId
    )}/steps/${headStep}`;
  }

  getRelayUrl(params: {
    boardId: string;
    attempt: number;
    resourceType: 'whiteboard' | 'database';
  }): RelaySupplierResult {
    const { boardId, resourceType } = params;
    const ariResourceType = resourceType === 'whiteboard' ? 'board' : resourceType;
    // `yarn start --ncs` sets VITE_NCS=true, which points the browser directly
    // at the NCS staging site (VITE_LOCAL_SITE, derived from
    // VITE_CANVAS_EAP_NCS_SITE in .env.eap), bypassing the Vite proxy.
    // Without it we fall back to same-origin (proxied via Vite in
    // `start:all --ncs`) or VITE_NCS_SESSION_URL for non-browser envs (tests).
    let ncsBasePath: string | undefined;
    if (process.env.VITE_NCS === 'true') {
      ncsBasePath = process.env.VITE_LOCAL_SITE;
    } else if (typeof globalThis.location !== 'undefined') {
      ncsBasePath = `${globalThis.location.protocol}//${globalThis.location.host}`;
    } else {
      ncsBasePath = process.env.VITE_NCS_SESSION_URL;
    }

    if (!ncsBasePath) {
      throw new Error('ncsBasePath is not defined.');
    }

    const { activationId, tenantId } = getSessionData();

    // In local dev (`start:all --ncs`) the URL uses a session-ARI path that
    // the local NCS container understands. `yarn start --ncs` and production
    // use the PMR path with real tenantId/activationId instead.
    const isLocal =
      process.env.NODE_ENV === 'development' && process.env.VITE_NCS !== 'true';
    if (isLocal) {
      const sessionAri = `ari:cloud:canvas:${tenantId}:${ariResourceType}/${boardId}`;
      return {
        url: `${ncsBasePath}/ncs-canvas/session/${sessionAri}`,
      };
    }

    return {
      url: `${ncsBasePath}/ncs-canvas/${tenantId}/${activationId}/confluence`,
      namespace: `ncs-canvas/session/ari:cloud:canvas:${tenantId}:${ariResourceType}/${boardId}`,
    };
  }
}
